import { useState } from "react";
import type React from "react";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { uploadChatbotReferenceImage, type ChatbotReferenceImage } from "../../services/chatbot.service";
import { optimizeReferenceImage } from "./chatbotImageUtils";
import { CHATBOT_REFERENCE_IMAGE_LIMIT } from "./constants";

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface UseChatbotReferenceImagesOptions {
  isStreaming: boolean;
  t: Translate;
}

export interface UseChatbotReferenceImagesReturn {
  isUploadingReferenceImages: boolean;
  referenceImages: ChatbotReferenceImage[];
  referenceImageUploadError: string | null;
  setReferenceImages: React.Dispatch<React.SetStateAction<ChatbotReferenceImage[]>>;
  setReferenceImageUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  handleReferenceImagesSelected: (files: FileList | null) => void;
  handleRemoveReferenceImage: (imageUrl: string) => void;
}

export const useChatbotReferenceImages = ({
  isStreaming,
  t,
}: UseChatbotReferenceImagesOptions): UseChatbotReferenceImagesReturn => {
  const [isUploadingReferenceImages, setIsUploadingReferenceImages] = useState(false);
  const [referenceImages, setReferenceImages] = useState<ChatbotReferenceImage[]>([]);
  const [referenceImageUploadError, setReferenceImageUploadError] = useState<string | null>(null);

  const handleReferenceImagesSelected = (files: FileList | null): void => {
    if (!files?.length || isStreaming) return;

    const availableSlots = Math.max(CHATBOT_REFERENCE_IMAGE_LIMIT - referenceImages.length, 0);
    const nextFiles = Array.from(files);
    if (availableSlots <= 0) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_full", { count: CHATBOT_REFERENCE_IMAGE_LIMIT }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }
    if (nextFiles.length > availableSlots) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_selection", {
          count: CHATBOT_REFERENCE_IMAGE_LIMIT,
          remaining: availableSlots,
          selected: nextFiles.length,
        }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }

    setIsUploadingReferenceImages(true);
    setReferenceImageUploadError(null);

    void Promise.all(
      nextFiles.map(async (file) => uploadChatbotReferenceImage(await optimizeReferenceImage(file)))
    )
      .then((uploadedImages) => {
        setReferenceImages((current) =>
          [...current, ...uploadedImages].slice(0, CHATBOT_REFERENCE_IMAGE_LIMIT)
        );
      })
      .catch((error: Error) => {
        setReferenceImageUploadError(error.message || t("chatbot_error_message"));
      })
      .finally(() => {
        setIsUploadingReferenceImages(false);
      });
  };

  const handleRemoveReferenceImage = (imageUrl: string): void => {
    setReferenceImages((current) => current.filter((image) => image.image_url !== imageUrl));
    setReferenceImageUploadError(null);
  };

  return {
    isUploadingReferenceImages,
    referenceImages,
    referenceImageUploadError,
    setReferenceImages,
    setReferenceImageUploadError,
    handleReferenceImagesSelected,
    handleRemoveReferenceImage,
  };
};
